import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {divIcon, LatLng} from 'leaflet';
import {useMap, useMapEvents} from 'react-leaflet';
import LeafletMarker from "@ui/leafletCustom/leafletMarker/LeafletMarker";

/** Screen distance between two arrows; keeps the density the same at any zoom. */
const SPACING_PX = 110

interface IArrow {
	position: LatLng
	angle: number
}

const buildIcon = (color: string, angle: number) => divIcon({
	className: '',
	iconSize: [16, 16],
	iconAnchor: [8, 8],
	html: `<svg width="16" height="16" viewBox="0 0 12 12" style="transform: rotate(${angle}deg); display: block; pointer-events: none">`
		+ `<path d="M2 1.5 L10.5 6 L2 10.5 L4.5 6 Z" fill="${color}" stroke="#fff" stroke-width="1.4" stroke-linejoin="round"/></svg>`,
})

interface IProps {
	positions: LatLng[]
	color: string
}

/**
 * Arrows along a route, so a stretch the route runs both ways reads as two directions rather than
 * one thick line. Placed by screen distance and only inside the visible area, recomputed on zoom/pan.
 */
const RouteArrowsSyncVoyageTemplate = ({positions, color}: IProps) => {

	const map = useMap()
	const [arrows, setArrows] = useState<IArrow[]>([])

	const recompute = useCallback(() => {
		if (positions.length < 2) {
			setArrows([])
			return
		}
		const visible = map.getBounds().pad(0.2)
		const result: IArrow[] = []
		let carried = SPACING_PX / 2

		for (let i = 0; i < positions.length - 1; i++) {
			const from = map.latLngToLayerPoint(positions[i])
			const to = map.latLngToLayerPoint(positions[i + 1])
			const length = from.distanceTo(to)
			if (length === 0) {
				continue
			}
			const angle = Math.atan2(to.y - from.y, to.x - from.x) * 180 / Math.PI
			let along = carried
			while (along <= length) {
				const ratio = along / length
				const position = map.layerPointToLatLng(from.add(to.subtract(from).multiplyBy(ratio)))
				if (visible.contains(position)) {
					result.push({position, angle})
				}
				along += SPACING_PX
			}
			carried = along - length
		}
		setArrows(result)
	}, [map, positions])

	useEffect(() => {
		recompute()
	}, [recompute])

	useMapEvents({
		zoomend: recompute,
		moveend: recompute,
	})

	const icons = useMemo(() => {
		return arrows.map((arrow) => buildIcon(color, arrow.angle))
	}, [arrows, color])

	return (
		<>
			{arrows.map((arrow, index) => (
				<LeafletMarker
					key={index}
					position={arrow.position}
					icon={icons[index]}
					keyboard={false}
				/>
			))}
		</>
	);
};

export default RouteArrowsSyncVoyageTemplate;
